/**
 * Central Zustand store for all game state — persisted to localStorage.
 *
 * Design mirrors chat_interface/weChiliStore patterns:
 * - `partialize` selects only the fields worth persisting (no transient UI state)
 * - `onRehydrateStorage` clears transient flags that shouldn't survive page refresh
 * - Append-only helpers for messages to avoid the "replace-all" flickering bug
 *
 * Components read rendering state directly via `useGameStore()`.
 * Async orchestration code (useGameLoop) accesses fresh state via `useGameStore.getState()`.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Character, ChatMessage, GameCard, GameResult, ActiveBuff } from '../types';
import { INITIAL_CP } from '../constants';
import { calculateTotalCompatibility } from '@shared/constants/compatibility';

// ── Public type aliases ──────────────────────────────────────────────
export type GameStep = 'MENU' | 'SELECT' | 'GAME' | 'RESULT';
export type GamePhase = 'WAITING_FOR_AI' | 'SWAP_CARDS' | 'DISCARD_CARDS' | 'PLAYER_TURN' | 'PROCESSING';

// ── Store shape ──────────────────────────────────────────────────────
interface GameStoreState {
  // Persistent
  step: GameStep;
  charA: Character | null;
  charB: Character | null;
  firstSpeaker: 'A' | 'B' | null;
  turn: number;
  cpA: number;  // Character A's affection toward B
  cpB: number;  // Character B's affection toward A
  messages: ChatMessage[];
  pastEvents: string[];
  
  // Card system
  swapCount: number;          // Number of manual swaps used (max MAX_SWAPS)
  pendingCards: GameCard[];   // Cards waiting to be added (during discard phase)
  deck: GameCard[];           // Card pool (grows via auto-gen every DRAFT_INTERVAL)
  cards: GameCard[];          // Current hand (max MAX_HAND_SIZE)
  
  // Persistent buffs from 人设卡
  activeBuffsA: ActiveBuff[];  // Buffs on character A
  activeBuffsB: ActiveBuff[];  // Buffs on character B
  
  phase: GamePhase;
  gameResult: GameResult | null;

  // Transient (excluded from persist via partialize)
  isTyping: boolean;
  typingSpeaker: Character | null;
  loadingText: string;
  selectedCard: GameCard | null;
}

interface GameStoreActions {
  // Navigation
  goToMenu: () => void;
  goToSelect: () => void;
  startGame: (charA: Character, charB: Character, firstSpeaker: 'A' | 'B') => void;
  endGame: (result: GameResult) => void;
  resetToSelect: () => void;

  // Game mutations
  setTurn: (turn: number) => void;
  setCpA: (cpA: number) => void;
  setCpB: (cpB: number) => void;
  appendMessage: (msg: ChatMessage) => void;
  appendMessages: (msgs: ChatMessage[]) => void;
  appendEvent: (event: string) => void;
  
  // Card system
  incrementSwapCount: () => void;
  setPendingCards: (cards: GameCard[]) => void;
  setDeck: (cards: GameCard[]) => void;
  addCardsToDeck: (cards: GameCard[]) => void;
  removeCardFromDeck: (cardId: string) => void;
  setCards: (cards: GameCard[]) => void;
  removeCardFromHand: (cardId: string) => void;
  moveCardsToHand: (cardIds: string[]) => void;
  
  // Buff management
  appendBuffA: (buff: ActiveBuff) => void;
  appendBuffB: (buff: ActiveBuff) => void;
  
  setPhase: (phase: GamePhase) => void;

  // Transient UI
  setIsTyping: (v: boolean) => void;
  setTypingSpeaker: (s: Character | null) => void;
  setLoadingText: (t: string) => void;
  setSelectedCard: (c: GameCard | null) => void;
}

export type GameStore = GameStoreState & GameStoreActions;

// ── Constants ────────────────────────────────────────────────────────
export const MAX_DECK_SIZE = 50;  // Large cap; deck grows via auto-gen every DRAFT_INTERVAL

// ── Initial (default) state ──────────────────────────────────────────
const INITIAL_STATE: GameStoreState = {
  step: 'MENU',
  charA: null,
  charB: null,
  firstSpeaker: null,
  turn: 1,
  cpA: INITIAL_CP,
  cpB: INITIAL_CP,
  messages: [],
  pastEvents: [],
  
  // Card system
  swapCount: 0,
  pendingCards: [],
  deck: [],
  cards: [],
  activeBuffsA: [],
  activeBuffsB: [],
  
  phase: 'WAITING_FOR_AI',
  gameResult: null,
  isTyping: false,
  typingSpeaker: null,
  loadingText: '',
  selectedCard: null,
};

// ── Storage key ──────────────────────────────────────────────────────
const STORAGE_KEY = 'cyber-matchmaker-game';

// ── Store ────────────────────────────────────────────────────────────
export const useGameStore = create<GameStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      // ── Navigation ─────────────────────────────────────────────────
      goToMenu: () => set({ ...INITIAL_STATE }),
      goToSelect: () => set({ step: 'SELECT' }),

      startGame: (charA, charB, firstSpeaker) => {
        // Compatibility-adjusted initial CP (same modifier for both sides)
        const modifier = calculateTotalCompatibility(
          charA.zodiac, charB.zodiac, charA.mbti, charB.mbti,
        );
        const initialCp = Math.min(100, Math.max(0, INITIAL_CP + modifier));

        set({
          step: 'GAME',
          charA,
          charB,
          firstSpeaker,
          turn: 1,
          cpA: initialCp,
          cpB: initialCp,
          messages: [],
          pastEvents: [],
          swapCount: 0,
          pendingCards: [],
          deck: [],
          cards: [],
          activeBuffsA: [],
          activeBuffsB: [],
          phase: 'WAITING_FOR_AI',
          gameResult: null,
          // Clear transient
          isTyping: false,
          typingSpeaker: null,
          loadingText: '',
          selectedCard: null,
        });
      },

      endGame: (result) => set({ step: 'RESULT', gameResult: result }),

      resetToSelect: () => set({ ...INITIAL_STATE, step: 'SELECT' }),

      // ── Game mutations ─────────────────────────────────────────────
      setTurn: (turn) => set({ turn }),
      setCpA: (cpA) => set({ cpA }),
      setCpB: (cpB) => set({ cpB }),

      appendMessage: (msg) =>
        set((s) => ({ messages: [...s.messages, msg] })),

      appendMessages: (msgs) =>
        set((s) => ({ messages: [...s.messages, ...msgs] })),

      appendEvent: (event) =>
        set((s) => ({ pastEvents: [...s.pastEvents, event] })),

      // ── Card system ────────────────────────────────────────────────
      incrementSwapCount: () =>
        set((s) => ({ swapCount: s.swapCount + 1 })),
      
      setPendingCards: (pendingCards) => set({ pendingCards }),
      
      setDeck: (deck) => set({ deck }),
      
      addCardsToDeck: (cards) =>
        set((s) => ({
          deck: [...s.deck, ...cards].slice(0, MAX_DECK_SIZE),
        })),
      
      removeCardFromDeck: (cardId) =>
        set((s) => ({
          deck: s.deck.filter((c) => c.id !== cardId),
        })),
      
      setCards: (cards) => set({ cards }),
      
      removeCardFromHand: (cardId) =>
        set((s) => ({
          cards: s.cards.filter((c) => c.id !== cardId),
        })),
      
      moveCardsToHand: (cardIds) =>
        set((s) => {
          const cardsToMove = s.deck.filter((d) => cardIds.includes(d.id));
          return { cards: [...s.cards, ...cardsToMove] };
        }),

      // ── Buff management ────────────────────────────────────────────
      appendBuffA: (buff) =>
        set((s) => ({ activeBuffsA: [...s.activeBuffsA, buff] })),

      appendBuffB: (buff) =>
        set((s) => ({ activeBuffsB: [...s.activeBuffsB, buff] })),

      setPhase: (phase) => set({ phase }),

      // ── Transient UI ───────────────────────────────────────────────
      setIsTyping: (v) => set({ isTyping: v }),
      setTypingSpeaker: (s) => set({ typingSpeaker: s }),
      setLoadingText: (t) => set({ loadingText: t }),
      setSelectedCard: (c) => set({ selectedCard: c }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),

      // Only persist the meaningful game state — not transient UI flags
      partialize: (state) => ({
        step: state.step,
        charA: state.charA,
        charB: state.charB,
        firstSpeaker: state.firstSpeaker,
        turn: state.turn,
        cpA: state.cpA,
        cpB: state.cpB,
        messages: state.messages,
        pastEvents: state.pastEvents,
        swapCount: state.swapCount,
        pendingCards: state.pendingCards,
        deck: state.deck,
        cards: state.cards,
        activeBuffsA: state.activeBuffsA,
        activeBuffsB: state.activeBuffsB,
        phase: state.phase,
        gameResult: state.gameResult,
      }),

      // On rehydration: clear transient flags that shouldn't survive refresh
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.isTyping = false;
        state.typingSpeaker = null;
        state.loadingText = '';
        state.selectedCard = null;
      },
    },
  ),
);
